import moment from 'moment'

export const getSubtaskNameFromPath = (pathValue) => {
    if (!pathValue) {
        return ""
    }
    const slashIndex = pathValue.indexOf('/')
    if (slashIndex === -1) {
        return pathValue
    }
    return pathValue.slice(0, slashIndex)
}

export const getSubsubNameFromPath = (pathValue) => {
    if (!pathValue) {
        return ""
    }
    const slashIndex = pathValue.indexOf('/')
    if (slashIndex === -1) {
        return ""
    }
    return pathValue.slice(slashIndex + 1)
}

export const getSubsubInfoFromHighlight = (highlighted, subtask) => {
    if (!highlighted) {
        return { subsubName: "", subsubSn: "", subsubPath: "" }
    }
    const slashIndex = highlighted.indexOf('/')
    if (slashIndex === -1) {
        return { subsubName: "", subsubSn: "", subsubPath: highlighted }
    }
    const subsubName = highlighted.slice(slashIndex + 1)
    let subsubSn = ""
    if (subtask && subtask.subsubs) {
        const matchedSubsub = subtask.subsubs.find((ss) => ss.name === subsubName)
        if (matchedSubsub && matchedSubsub.sn) {
            subsubSn = matchedSubsub.sn
        }
    }
    return { subsubName, subsubSn, subsubPath: highlighted }
}

export const createTimerHistoryEntry = ({
    subtask,
    highlighted,
    duration,
    done = moment().toDate(),
    id = Math.floor(Math.random() * 1000000000)
}) => {
    const highlightInfo = getSubsubInfoFromHighlight(highlighted, subtask)
    const subtaskSname = subtask?.sname || getSubtaskNameFromPath(highlighted)
    return {
        'subsub': subtaskSname,
        'subtaskSname': subtaskSname,
        'subtaskSn': subtask?.sn || "",
        'subsubPath': highlightInfo.subsubPath,
        'subsubName': highlightInfo.subsubName,
        'subsubSn': highlightInfo.subsubSn,
        'done': done,
        'duration': duration,
        'id': id,
        'tags': subtask?.tags ? subtask.tags : "",
        'allowFeedback': subtask?.allowFeedback > 0 ? subtask.allowFeedback : 0,
        'note': highlighted != null ? highlighted : "",
        'bonusCoeff': subtask?.bonusCoeff ? subtask.bonusCoeff : 1
    }
}

export const buildReportFromTimerHistory = (timerHistory, stampId) => {
    const reportMap = new Map()

    timerHistory.forEach((th) => {
        const pathValue = th.subsubPath || th.note || ""
        const subtaskSname = th.subtaskSname || th.subsub || getSubtaskNameFromPath(pathValue)
        const subtaskSn = th.subtaskSn || ""
        const subsubSn = th.subsubSn || ""
        const subsubName = th.subsubName || getSubsubNameFromPath(pathValue)
        const groupKey = `${subtaskSn || subtaskSname}::${subsubSn || subsubName || ""}`

        if (!reportMap.has(groupKey)) {
            reportMap.set(groupKey, {
                subtaskSname,
                subtaskSn,
                subsubSn,
                subsubName,
                subsubPath: pathValue,
                time: 0,
                tags: "",
                allowFeedback: 0,
                bonusCoeff: 1,
                clientEntryIds: [],
                goalCompletionIds: []
            })
        }

        const reportEntry = reportMap.get(groupKey)
        reportEntry.time += th.duration
        if (th.id !== null && th.id !== undefined) {
            reportEntry.clientEntryIds.push(String(th.id))
        }
        if (th.goalCompletionId) {
            reportEntry.goalCompletionIds.push(String(th.goalCompletionId))
        }
        if (th.hasOwnProperty('tags') && th.tags !== null && th.tags !== undefined) {
            reportEntry.tags = th.tags
        }
        if (th.allowFeedback) {
            reportEntry.allowFeedback = th.allowFeedback
        }
        if (th.bonusCoeff) {
            reportEntry.bonusCoeff = th.bonusCoeff
        }
    })

    return Array.from(reportMap.values()).map((entry) => {
        return {
            sname: entry.subtaskSname,
            subtaskSn: entry.subtaskSn,
            subsubSn: entry.subsubSn,
            subsubName: entry.subsubName,
            subsubPath: entry.subsubPath,
            stampId: stampId,
            time: entry.time,
            tags: entry.tags,
            allowFeedback: entry.allowFeedback,
            bonusCoeff: entry.bonusCoeff,
            clientEntryIds: entry.clientEntryIds,
            goalCompletionIds: entry.goalCompletionIds
        }
    })
}

export const buildTimeReportDocument = (report, date = moment().toDate()) => {
    return { v: 2, date: date, report: report }
}

export const createTimeReportId = (date = moment()) => {
    return moment(date).format("DD-MM-YYYY-HH-mm-ss")
}

export const serializeTimerHistoryForServer = (timerHistory, taskId, reportId = '') => {
    return timerHistory.map((entry) => {
        return {
            'datetime': moment(entry.done).toISOString(),
            'taskId': taskId,
            'duration': entry.duration / 60000,
            'note': entry.note != null ? entry.note : "",
            'bonusCoeff': entry.bonusCoeff ? entry.bonusCoeff : 1,
            'subtaskSn': entry.subtaskSn ? entry.subtaskSn : "",
            'subtaskSname': entry.subtaskSname ? entry.subtaskSname : "",
            'subsubSn': entry.subsubSn ? entry.subsubSn : "",
            'subsubName': entry.subsubName ? entry.subsubName : "",
            'subsubPath': entry.subsubPath ? entry.subsubPath : "",
            'clientEntryId': entry.id !== null && entry.id !== undefined ? String(entry.id) : "",
            'goalCompletionId': entry.goalCompletionId ? String(entry.goalCompletionId) : "",
            'reportId': entry.reportId || reportId || ""
        }
    })
}

export const submitTimerHistoryToServer = async ({
    apiUrl,
    token,
    timerHistory,
    taskId,
    reportId = '',
    goalCompletions = [],
    fetchImpl = fetch
}) => {
    const timerHistoryToSubmit = serializeTimerHistoryForServer(timerHistory, taskId, reportId)
    const objectToSubmit = { data: JSON.stringify(timerHistoryToSubmit) }
    if (reportId || goalCompletions.length > 0) {
        objectToSubmit.goalData = JSON.stringify({ reportId, completions: goalCompletions })
    }
    const formData = new URLSearchParams()

    for (const [key, value] of Object.entries(objectToSubmit)) {
        if (value !== null && value !== undefined) {
            formData.append(key, value)
        } else {
            formData.append(key, "")
        }
    }

    return fetchImpl(apiUrl + '/saveTimerHistory?token=' + token, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
    })
}
